<template>
  <div class="relative rounded-[8px] px-3 md:px-6 ring-1 ring-gray-light bg-gray-5">
    <Expandable :expanded="false">
      <template #default="{ toggle, expanded }">
        <div class="flex items-center justify-between py-1 md:py-4">
          <div class="flex items-center gap-4 md:gap-8">
            <div class="border-solid border-r border-gray-light pr-4 md:pr-8 md:min-w-[132px]">
              <p class="text-b1 text-caption">Ärendenummer</p>
              <p class="text-b2 md:text-b1 md:font-bold">{{ item.code }}</p>
            </div>

            <div class="flex gap-3 md:gap-8">
              <p class="text-b2 md:text-b1 hidden md:block" v-if="item.registeredDate && !isArchived">
                {{ formatDate(item.registeredDate) }}
              </p>
              <p class="text-b2 md:text-b1 hidden md:block" v-else-if="item.lastUpdatedDate && !isArchived">
                {{ formatDate(item.lastUpdatedDate) }}
              </p>
              <p class="text-b2 md:text-b1">
                {{ getWhere(item) }}
              </p>
              <p class="text-b2 md:text-b1 hidden md:block">
                {{ formatAdress(item.address) || formatAdress(item.location) }}
              </p>

            </div>
          </div>
          <div class="text-label absolute -top-100 right-6">
            <span v-if="!isArchived && item.status === 'Registrerad' || item.status === 'new'" class="font-neue-kabel bg-dark-turquoise-10 px-2 py-1 rounded-sm">
              Registrerad
            </span>
            <span v-if="!isArchived && item.status === 'Påbörjad' || item.status === 'ongoing'" class="font-neue-kabel bg-primary-30 px-2 py-1 rounded-sm">
              Påbörjad
            </span>
            <span v-if="!isArchived && (item.status === 'Utförd' || item.status === 'finished')" class="font-neue-kabel bg-green-10 px-2 py-1 rounded-sm">Avslutad</span>
            <span v-if="!isArchived && item.status === 'parked'" class="font-neue-kabel bg-white px-2 py-1 rounded-[2px] border border-solid border-gray-30">Pausad</span>

            <span
              v-if="isArchived"
              class="px-2 py-1 bg-green-10 rounded-sm"
            >
              Avslutad
            </span>
            <span v-if="!isArchived && item.status === 'Status okänd'" class="bg-dark-turquoise-10 px-2 py-1 rounded-sm">
              Status okänd
            </span>
          </div>
          <Button variant="tertiary" @click="toggle()">
           <span class="hidden xl:inline">
             {{ expanded ? 'Dölj detaljer' : 'Visa detaljer' }}
           </span>
            <template #icon-after>
              <!-- Mobile icon -->
              <span class="xl:hidden">
                <component :is="expanded ? 'IconMinus' : 'IconAdd'" />
              </span>
              <!-- Desktop icon -->
              <span class="hidden xl:inline">
                <IconChevronDown />
              </span>
            </template>
          </Button>
        </div>
      </template>
      <template #body="{ toggle, expanded }">
        <div class="border-t border-solid border-gray-light py-6 flex flex-col gap-2">
          <h3 class="text-t6">Status för din felanmälan</h3>

          <div class="flex flex-col">
            <div class="ml-[56px] relative py-2" v-if="item.finishedDate">
              <Status status="finished" :isLast="false" />
              <div>
                <h3 class="h-medium">Ditt ärende har avslutats</h3>
                <p class="date">{{ formatDateWithTime(item.finishedDate) }}</p>
                <div v-if="item.status=='finished'" v-html="statusTexts.errorreport_status_finished"></div>

                <div class="bg-[#F5F5F5] border border-solid border-gray-30 rounded-[4px] px-3 pb-2 pt-1 mt-2" v-if="(item.internalStatus || item.responsibleName) && item.status=='finished'">
                  <h4 class="h-small" v-if="item.responsibleName">Ansvarig</h4><p>{{item.responsibleName}}</p>
                </div>

              </div>
            </div>

            <div class="ml-[56px] relative py-2" v-if="item.status=='parked'">
              <Status status="parked" :isLast="false" />
              <div>
                <h3 class="h-medium">Ditt ärende är pausat</h3>
                <div v-html="statusTexts.errorreport_status_paused"></div>
                <p class="date" v-if="item.nextActionDate">Nästa händelse {{ formatDate(item.nextActionDate) }}</p>

                <div class="bg-[#F5F5F5] border border-solid border-gray-30 rounded-[4px] px-3 pb-2 pt-1 mt-2" v-if="item.parkingReason || item.responsibleName || item.internalStatus">
                  <h4 class="h-small" v-if="item.responsibleName">Ansvarig</h4><p>{{item.responsibleName}}</p>
                  <h4 class="h-small mt-2" v-if="item.parkingReason">Kommentar</h4><p>{{item.parkingReason}}</p>
                  <h4 class="h-small mt-2" v-if="item.internalStatus">Kommentar</h4><p>{{item.internalStatus}}</p>
                </div>
              </div>
            </div>

            <div class="ml-[56px] relative py-2" v-if="item.startDate">
              <Status status="ongoing" :isLast="false" />
              <div>
                <h3 class="h-medium">Ditt ärende har påbörjats</h3>
                <p class="date">{{formatDateWithTime(item.startDate)}}</p>
                <div v-if="item.status=='ongoing'" v-html="statusTexts.errorreport_status_started"></div>

                <div class="bg-[#F5F5F5] border border-solid border-gray-30 rounded-[4px] px-3 pb-2 pt-1 mt-2" v-if="(item.internalStatus || item.responsibleName) && item.status=='ongoing'">
                  <h4 class="h-small" v-if="item.responsibleName">Ansvarig</h4><p>{{item.responsibleName}}</p>
                  <h4 class="h-small mt-2" v-if="item.internalStatus">Kommentar</h4><p>{{item.internalStatus}}</p>
                </div>
              </div>
            </div>

            <div class="ml-[56px] relative py-2" v-if="item.registeredDate">
              <Status status="new" :isLast="true" />
              <div>
                <h3 class="h-medium">Ditt ärende har registrerats</h3>
                <p class="date">{{formatDateWithTime(item.registeredDate)}}</p>
                <div v-if="item.status=='new'" v-html="statusTexts.errorreport_status_registered"></div>

                <div class="bg-[#F5F5F5] border border-solid border-gray-30 rounded-[4px] px-3 pb-2 pt-1 mt-2" v-if="item.status == 'new' && item.internalStatus">
                  <h4 class="h-small mt-2" v-if="item.internalStatus">Kommentar</h4><p>{{item.internalStatus}}</p>
                </div>
              </div>
            </div>
          </div>


          <div class="border-t border-gray-light pt-6 mt-4">
            <h3 class="text-t6 mb-3">Om felet</h3>

            <h4 class="h-medium mb-1 mt-3">Var är felet</h4>
            <p v-html="getDesc(item)"></p>

            <h4 class="h-medium mb-1 mt-3" v-if="getLocation(item)">Vad är fel</h4>
            <p v-html="getLocation(item)"></p>


            <h4 class="h-medium mb-1 mt-3" v-if="!!item.description">Beskrivning</h4>
            <p>{{ item.description && item.description.split("Jag nås")[0].trim() }}</p>

            <h4 class="h-medium mb-1 mt-3" v-if="!!item.accessCaption || !!item.pet">Tillträde</h4>
            <p v-if="!!item.accessCaption">{{ item.accessCaption }}</p>
            <p v-if="!!item.pet">Husdjur: {{item.pet}}</p>
          </div>


          <!--          <ul>
                    <li v-if="!!item.workOrderRows" v-for="row in item.workOrderRows">
                      <div>
                        {{ row.description }}
                      </div>
                      <div class="font-sm">
                        <span v-if="row.locationCode">{{ row.locationCode }}</span>
                        <span v-if="row.partOfBuildingCode"> > {{ row.partOfBuildingCode }}</span>
                      </div>
                    </li>
                    <li v-else>
                      Vi saknar tyvärr information om vad felanmälan innehåller.
                    </li>
                    </ul>-->
<!--
          <p>Tillträde: {{ fixAccessCaption(item.accessCaption) }}</p>

-->






        </div>
      </template>
    </Expandable>
  </div>
</template>
<script>
import Expandable from '@/components/ui/expandable';
import Status from '@/components/ui/status';
import Box from '@/components/ui/box';
import IconChevronDown from '@/components/icons/IconChevronDown';
import IconAdd from '@/components/icons/IconAdd';
import IconMinus from '@/components/icons/IconMinus';
import Button from '@/components/ui/button';
import dayjs from 'dayjs';

export default {
  name: 'WorkorderItem',
  components: { Expandable, Box, Button, IconChevronDown, IconAdd, IconMinus, Status },
  props: {
    item: {
      type: Object,
      required: true,
    },
    isArchived: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    statusTexts() {
      return this.$store.getters.webapp_config.msg;
    },
  },
  data() {
    return {};
  },
  methods: {
    fixAccessCaption(accessCaption) {
      return accessCaption === null ? "Laddar tillträde..." : accessCaption;
    },

    formatTitle(title) {
      if (!title) return;
      let altTitle = title.split(" - ");
      return altTitle[0].split('Från webb: ').join('');
    },

    /**
     * Skadedjur har kategorierna Skadedjur - plats - skadedjur, medan övriga har plats - del - fel.
     * Titeln börjar då med "Skadedjur" och platsen ligger i categoryThree.
     */
    isPestControl(workorder) {
      return workorder?.categoryTwo === 'Skadedjur';
    },

    getWhere(item) {
      return (this.isPestControl(item) && item.categoryThree) || this.formatTitle(item.title);
    },

    getDesc(item) {
      const where = this.getWhere(item);

      if (this.formatAdress(item.location))
        return "<span class='border-r-solid border-r border-gray-light pr-2 mr-2'>" + where + "</span>" + this.formatAdress(item.location);

      return "<span>" + where + "</span>"
    },

    getLocation(workorder) {
      if (!workorder) return;
      const altTitle = this.isPestControl(workorder)
        ? [workorder.categoryTwo, workorder.categoryFour].reverse()
        : [workorder.categoryFour, workorder.categoryFive].reverse();
      if (!altTitle || altTitle.length == 0 || (workorder.categoryFour == "" && workorder.categoryFive == "")) return;
      return "<span class='border-r-solid border-r border-gray-light pr-2 mr-2'>" + altTitle.pop() + "</span>" +  altTitle.pop() || '';
    },

    formatAdress(adr) {
      if (!adr) return;
      let altTitle = adr.split(" - ");
      return altTitle[1];
    },

    isSameDate(d1, d2) {
      return dayjs(d1).format('YYYY-MM-DD HH:mm') === dayjs(d2).format('YYYY-MM-DD HH:mm');
    },

    formatDate(date) {
      if (!date) return '';
      return dayjs(date).format('YYYY-MM-DD');
    },

    formatDateWithTime(date) {
      if (!date) return '';
      return dayjs(date).format('YYYY-MM-DD HH:mm');
    },
  },
};
</script>
